/**
 * DSD 테이블 레이아웃 훅
 *
 * border-separate 테이블에서 발생하는 이중 border 제거, rowspan border 보정,
 * 모서리 셀 radius 계산을 하나의 훅으로 캡슐화한다.
 *
 * 셀 컴포넌트(DsdCell, DsdHeaderCell)는 자체 border를 가지며,
 * 이 훅은 테이블 레이아웃 관점에서 필요한 className만 제공한다.
 */

import { cn } from "@/lib/utils";
import { useMemo } from "react";

// ============================================
// 타입
// ============================================

interface LayoutCell {
  cellId: string;
  rowspan?: number;
  colspan?: number;
}

interface LayoutRow {
  cells: LayoutCell[];
}

export interface DsdTableLayoutOptions {
  noBorder?: boolean;
  /** 단위 표시(예: "(단위: 천원)") nb 표 — 원본 폭이 화면보다 넓어도 가로 스크롤 없이 컨테이너 폭까지만 채운다 */
  isUnitTable?: boolean;
}

/**
 * 표 attrs에서 레이아웃 옵션을 뽑는다.
 *
 * 대사·리뷰가 같은 규칙을 쓰도록 판별을 한곳에 둔다 — 마커 이름이 바뀔 때
 * 한쪽만 고쳐도 타입 에러가 안 나는 문제를 없앤다.
 */
export function dsdTableLayoutOptionsFrom(
  attrs: Record<string, string>,
): DsdTableLayoutOptions {
  return {
    noBorder: attrs.class?.includes("nb") ?? false,
    // 백엔드는 단위 표일 때만 이 속성을 붙인다(값은 항상 "true", false를 내리지
    // 않는다). 존재 여부로 판단해 boolean attribute 직렬화에도 견디게 한다.
    isUnitTable: "data-unit-table" in attrs,
  };
}

interface DsdTableLayout {
  tableClassName: string;
  rowClassName: (rowIndex: number) => string | undefined;
  cellClassName: (cellId: string) => string | undefined;
}

// ============================================
// 테이블 CSS
// ============================================

/**
 * border-separate 둥근 테이블.
 *
 * 이중 border 방지: 모든 셀의 top/left를 제거하고, 첫 행에 top, 첫 열에 left를 복원.
 * 모서리 radius는 CSS 셀렉터 대신 computeVisualCorners()가 JS로 계산한다.
 */
const ROUNDED_TABLE = cn(
  "border-separate border-spacing-0 w-max",
  "[&_tr>*]:border-t-0 [&_tr>*]:border-l-0",
  "[&_tr:first-child>*]:border-t",
  "[&_tr>*:first-child]:border-l",
);

const NO_BORDER_TABLE_BASE =
  "border-separate border-spacing-0 [&_tr>*]:border-0";

/**
 * 경계선 없는 표. 단위 표시 표만 w-max(width:max-content)를 뺀다.
 *
 * w-max는 "항상 콘텐츠 크기 그대로"를 강제해 컨테이너보다 넓어도 줄어들지
 * 않는다(TableScrollArea가 가로 스크롤로 흡수). 단위 표는 반대로 원본 폭이
 * 화면보다 넓으면 줄어들길 원하므로, width를 auto로 두어 테이블 자동
 * 레이아웃이 컨테이너 폭에 맞춰 열을 비례 축소하게 한다(콘텐츠가 감당할
 * 만큼만 — 실측 확인, max-width로는 안 먹힘: TableScrollArea의 Radix
 * viewport 내부 래퍼가 display:table로 콘텐츠 크기에 맞춰 같이 늘어나
 * percentage 기준 자체가 무의미해진다).
 */
const noBorderTableClassName = (isUnitTable: boolean) =>
  cn(NO_BORDER_TABLE_BASE, !isUnitTable && "w-max");

// ============================================
// rowspan border 보정
// ============================================

/** rowspan으로 인해 0번 열이 점유된 row 인덱스 집합 */
function buildFirstColSpannedRows(rows: LayoutRow[]): Set<number> {
  const spanned = new Set<number>();
  let remaining = 0;
  for (let i = 0; i < rows.length; i++) {
    if (remaining > 0) {
      spanned.add(i);
      remaining--;
    }
    const firstCell = rows[i].cells[0];
    if (firstCell && (firstCell.rowspan ?? 1) > 1) {
      remaining = (firstCell.rowspan ?? 1) - 1;
    }
  }
  return spanned;
}

// ============================================
// 시각적 모서리 셀 계산
// ============================================

interface VisualCorners {
  tl: string | null;
  tr: string | null;
  bl: string | null;
  br: string | null;
}

/**
 * rowspan/colspan을 반영하여 테이블의 시각적 4 모서리에 위치한 cellId를 계산한다.
 * CSS :first-child/:last-child 셀렉터는 DOM 위치만 보므로 rowspan 시 오동작하는 문제를 해결.
 */
function computeVisualCorners(rows: LayoutRow[]): VisualCorners | null {
  if (rows.length === 0) return null;

  const totalCols = rows[0].cells.reduce((sum, c) => sum + (c.colspan ?? 1), 0);
  if (totalCols === 0) return null;

  // grid[row][col] = cellId
  const grid: (string | null)[][] = Array.from({ length: rows.length }, () =>
    Array<string | null>(totalCols).fill(null),
  );

  for (let ri = 0; ri < rows.length; ri++) {
    let col = 0;
    for (const cell of rows[ri].cells) {
      while (col < totalCols && grid[ri][col] !== null) col++;
      const rs = cell.rowspan ?? 1;
      const cs = cell.colspan ?? 1;
      for (let dr = 0; dr < rs && ri + dr < rows.length; dr++) {
        for (let dc = 0; dc < cs && col + dc < totalCols; dc++) {
          grid[ri + dr][col + dc] = cell.cellId;
        }
      }
      col += cs;
    }
  }

  const lastRow = rows.length - 1;
  const lastCol = totalCols - 1;
  return {
    tl: grid[0][0],
    tr: grid[0][lastCol],
    bl: grid[lastRow][0],
    br: grid[lastRow][lastCol],
  };
}

/** corners → cellId별 className 맵 */
function buildCornerClassMap(corners: VisualCorners): Map<string, string> {
  const map = new Map<string, string[]>();

  const add = (id: string | null, cls: string) => {
    if (!id) return;
    if (!map.has(id)) map.set(id, []);
    map.get(id)!.push(cls);
  };

  add(corners.tl, "rounded-tl-[6px]");
  add(corners.tr, "rounded-tr-[6px]");
  add(corners.bl, "rounded-bl-[6px]");
  add(corners.br, "rounded-br-[6px]");

  const result = new Map<string, string>();
  for (const [id, classes] of map) {
    result.set(id, [...classes, "overflow-hidden"].join(" "));
  }
  return result;
}

// ============================================
// 훅
// ============================================

export function useDsdTableLayout(
  rows: LayoutRow[],
  options?: DsdTableLayoutOptions,
): DsdTableLayout {
  const noBorder = options?.noBorder ?? false;
  const isUnitTable = options?.isUnitTable ?? false;

  const tableClassName = noBorder
    ? noBorderTableClassName(isUnitTable)
    : ROUNDED_TABLE;

  const spannedRows = useMemo(
    () => (noBorder ? new Set<number>() : buildFirstColSpannedRows(rows)),
    [rows, noBorder],
  );

  const cornerClassMap = useMemo(() => {
    if (noBorder) return new Map<string, string>();
    const corners = computeVisualCorners(rows);
    return corners ? buildCornerClassMap(corners) : new Map<string, string>();
  }, [rows, noBorder]);

  const rowClassName = useMemo(() => {
    return (rowIndex: number): string | undefined =>
      spannedRows.has(rowIndex) ? "[&>*:first-child]:!border-l-0" : undefined;
  }, [spannedRows]);

  const cellClassName = useMemo(() => {
    return (cellId: string): string | undefined => cornerClassMap.get(cellId);
  }, [cornerClassMap]);

  return { tableClassName, rowClassName, cellClassName };
}
